import { AdviseCurrentSupplier, AdviseSupplierInvoice } from '../../../../types/advise';
import { TextBlock } from '../../../Atoms/Text';
import { baselineExclusions, dollars } from './currentSupplierSpend';
import { BaselineFooterFrame } from './BaselineFooterFrame';
import { InvoiceBaselineFooter } from './InvoiceBaselineFooter';
import { mostRecentInvoice } from './InvoiceView.calc';
import { draftLinesOf, draftTotals, isBlankLine } from './manualSpendDraft.calc';
import { monthlyTotalError } from './currentSupplierLimits';
import { NotIncludedYet } from './NotIncludedYet';
import { ManualSpendDraft } from './useManualSpendDraft';

export interface ManualSpendFooterProps {
  supplier: AdviseCurrentSupplier;
  invoice: AdviseSupplierInvoice | null;
  draft: ManualSpendDraft;
}

// upstack_api counts only the most recent invoice's lines, so an older invoice's never count.
const draftCounts = ({ supplier, invoice, draft }: ManualSpendFooterProps) =>
  draft.entry === 'monthly_total' || !invoice || invoice.id === supplier.most_recent_invoice_id;

const countedTotals = (props: ManualSpendFooterProps) =>
  draftCounts(props)
    ? props.draft.totals
    : draftTotals(draftLinesOf(mostRecentInvoice(props.supplier)?.line_items ?? []));

// SupplierBaseline#complete?; saving here moves this invoice past manual entry.
const completes = (props: ManualSpendFooterProps) => {
  const { supplier, draft } = props;
  if (draft.entry === 'monthly_total') return !monthlyTotalError(draft.total);
  if (draftCounts(props)) return draft.lines.some((line) => line.in_scope && !isBlankLine(line));
  const recent = mostRecentInvoice(supplier);
  return !!recent && recent.status !== 'needs_manual_entry' && recent.line_items.some((line) => line.in_scope);
};

const runningTotal = (props: ManualSpendFooterProps) => {
  const { supplier, draft } = props;
  const { inScope, outOfScope } = countedTotals(props);
  const taxes = draft.entry === 'line_items' ? (mostRecentInvoice(supplier)?.taxes_and_surcharges ?? 0) : 0;
  const included = supplier.include_taxes ? taxes : 0;
  return {
    amount: Math.round((inScope + included) * 100) / 100,
    excluded: baselineExclusions(outOfScope, taxes - included)
  };
};

const changesBaseline = (props: ManualSpendFooterProps) =>
  props.draft.changed && (draftCounts(props) || props.supplier.spend_entry === 'monthly_total');

export const ManualSpendFooter = (props: ManualSpendFooterProps) => {
  if (!changesBaseline(props)) return <InvoiceBaselineFooter supplier={props.supplier} />;
  const { amount, excluded } = runningTotal(props);
  return (
    <BaselineFooterFrame excluded={excluded}>
      {completes(props) ? (
        <TextBlock
          size="xxl"
          weight="semibold"
          color="blue2">
          {dollars(amount)}
        </TextBlock>
      ) : (
        <NotIncludedYet />
      )}
    </BaselineFooterFrame>
  );
};
